export const projects = [
  {
    slug: "harborline-properties",
    name: "Harborline Properties",
    industry: "Real Estate · Development",
    concept: true,
    url: "/minisites/harborline/index.html",
    image: "/harborline-preview.jpeg",
    imgPos: "object-center",
    summary:
      "Positioning a boutique property group as the calm authority in a crowded market — architecture first, salesmanship second.",
  },
  {
    slug: "billingbuddy",
    name: "Billing Buddy",
    industry: "SaaS · Fintech",
    url: "https://billingbuddy.io",
    image: "/billingbuddy-preview.jpeg",
    imgPos: "object-center",
    summary:
      "Kronos designed and built the product site for this billing-automation platform — one clear pitch, a working demo flow, and a contact path that turns billing problems into booked calls.",
  },
  {
    slug: "ember-and-oak",
    name: "Ember & Oak",
    industry: "Restaurant · Hospitality",
    concept: true,
    url: "/minisites/ember-oak/index.html",
    image: "/ember-preview.jpeg",
    imgPos: "object-left",
    summary:
      "Transforming a wood-fired restaurant with strong word-of-mouth but zero online presence into a warm, reservation-ready digital home.",
  },
];

export const services = [
  {
    id: "web-strategy",
    title: "Web Strategy",
    tagline: "Strong websites begin with clarity.",
    description:
      "Before anything is designed, we define who the site must persuade, what those visitors need to understand, and what action the experience should drive.",
    items: ["Audience", "Goals", "Positioning", "User journey", "Conversion paths", "Content hierarchy"],
  },
  {
    id: "website-design",
    title: "Website Design",
    tagline: "Every page is designed intentionally — not assembled from a template.",
    description:
      "Custom responsive interfaces designed around your brand, your customers, and the specific actions your business needs visitors to take.",
    items: ["UI design", "UX", "Responsive layouts", "Design systems", "Interaction design", "Conversion structure"],
  },
  {
    id: "website-development",
    title: "Website Development",
    tagline: "Fast, clean, and built to last.",
    description:
      "Modern, scalable builds with performance, accessibility, and SEO foundations treated as requirements — not add-ons.",
    items: ["Responsive development", "Performance", "Accessibility", "Clean structure", "SEO foundations", "CMS integrations"],
  },
  {
    id: "branding",
    title: "Branding & Creative Direction",
    tagline: "Build a brand that looks as credible as the business behind it.",
    description:
      "Optional brand development for companies whose visual identity has fallen behind the quality of their work.",
    items: ["Logo refinement", "Typography", "Color systems", "Visual language", "Messaging", "Digital brand guidelines"],
  },
  {
    id: "conversion-optimization",
    title: "Conversion Optimization",
    tagline: "Turn attention into action.",
    description:
      "Structure, messaging, and interface decisions focused on one outcome: making the next step obvious for every visitor.",
    items: ["CTA strategy", "Landing pages", "Information hierarchy", "Lead capture", "Contact flows", "Analytics integration"],
  },
  {
    id: "maintenance",
    title: "Website Maintenance",
    tagline: "A website is never finished.",
    description:
      "An ongoing relationship for businesses that treat their website as infrastructure — kept fast, current, and improving.",
    items: ["Updates", "Content changes", "Performance monitoring", "Technical support", "Continuous improvements"],
  },
];

export const processSteps = [
  { num: "01", title: "Discovery", description: "Understand the company, customers, market, goals, and existing digital presence." },
  { num: "02", title: "Strategy", description: "Define positioning, sitemap, messaging hierarchy, content needs, and conversion goals." },
  { num: "03", title: "Design", description: "Create wireframes, visual direction, responsive screens, interaction patterns, and the brand system." },
  { num: "04", title: "Development", description: "Translate the design into a fast, responsive, production-ready website." },
  { num: "05", title: "Launch", description: "Test responsiveness, speed, forms, analytics, SEO fundamentals, and technical functionality." },
  { num: "06", title: "Evolution", description: "Continue refining the website using customer behavior, analytics, and business needs." },
];

export const backwardsSteps = [
  { num: "01", title: "Understand", description: "Business, customers, competition, positioning." },
  { num: "02", title: "Structure", description: "Pages, messaging, user journeys, conversion paths." },
  { num: "03", title: "Design", description: "Create a visual identity that reflects the value of the business." },
  { num: "04", title: "Build", description: "Develop a fast, responsive, polished experience." },
  { num: "05", title: "Refine", description: "Test, optimize, launch, and improve." },
];

export const whyKronos = [
  { title: "Business First", description: "We don't begin with a template. We begin with the business." },
  { title: "Custom by Default", description: "Every visual system is created around the client rather than forcing the company into a predefined layout." },
  { title: "Design + Development Together", description: "Design decisions remain grounded in how the final experience will actually function." },
  { title: "Conversion Built In", description: "A beautiful website is only useful if visitors know what to do next." },
  { title: "Mobile Matters", description: "Every project is designed around how modern customers actually browse." },
  { title: "Long-Term Thinking", description: "Build digital infrastructure capable of evolving alongside the company." },
];

export const principles = [
  { title: "Clarity Before Creativity", description: "Visitors should understand the business instantly." },
  { title: "Restraint Creates Confidence", description: "Not everything needs animation, color, or decoration." },
  { title: "Speed Is Design", description: "A beautiful website that loads slowly is poorly designed." },
  { title: "Mobile Is Not Secondary", description: "Mobile experiences should feel intentional, not adapted." },
  { title: "Every Page Needs a Purpose", description: "A page without a clear role should not exist." },
  { title: "Details Create Perception", description: "Spacing, typography, transitions, and responsiveness determine how premium a brand feels." },
];

export const industries = [
  "Professional Services",
  "Construction & Home Services",
  "Real Estate",
  "Hospitality",
  "Restaurants",
  "Healthcare",
  "Finance",
  "Technology",
  "E-Commerce",
  "Personal Brands",
];

export const results = [
  { title: "Sharper positioning", description: "Customers immediately understand what the company offers." },
  { title: "Higher perceived value", description: "A premium digital presence supports premium pricing." },
  { title: "Clearer conversion paths", description: "Visitors always know the next logical step." },
  { title: "Stronger mobile experience", description: "Designed for users browsing from phones first." },
];

export const tiers = [
  {
    name: "Launch",
    description: "For businesses establishing a strong professional digital presence.",
    items: ["Strategy & sitemap", "Custom responsive design", "Core development", "SEO foundations"],
  },
  {
    name: "Growth",
    description: "For established businesses needing stronger strategy, design, content, and conversion infrastructure.",
    items: ["Everything in Launch", "Brand refinement", "Conversion architecture", "Analytics & optimization"],
  },
  {
    name: "Custom",
    description: "For sophisticated websites requiring custom functionality, integrations, or deeper digital systems.",
    items: ["Everything in Growth", "Custom functionality", "Third-party integrations", "Ongoing evolution"],
  },
];

export const faqs = [
  {
    q: "How long does a website project take?",
    a: "Timing depends on scope, content readiness, revisions, and complexity. A focused marketing site typically moves faster than a custom build with integrations. We define a realistic timeline together before any work begins — and we keep it.",
  },
  {
    q: "Do you redesign existing websites?",
    a: "Yes. Redesigns are a large part of our work. We start by auditing what your current site does well, where it loses visitors, and what the business actually needs — then rebuild around that.",
  },
  {
    q: "Can you help with branding?",
    a: "Yes. Branding and creative direction are part of our offering, from logo refinement and typography to complete visual systems and digital brand guidelines.",
  },
  {
    q: "Do you write website copy?",
    a: "We help shape messaging and content hierarchy as part of strategy, and can assist with strategic copywriting so the site says the right things in the right order.",
  },
  {
    q: "Will the website work on mobile?",
    a: "Every project is designed responsively from the start — not adapted afterward. Mobile layouts, touch targets, and load performance are treated as primary design constraints.",
  },
  {
    q: "Can I update the website after launch?",
    a: "Yes. Sites can be built with content management capabilities so your team handles day-to-day updates, and we offer ongoing maintenance for everything else.",
  },
  {
    q: "Do you handle SEO?",
    a: "We build in foundational technical and on-page SEO — semantic structure, metadata, performance, and clean architecture. We don't promise rankings; we build the conditions that make them possible.",
  },
  {
    q: "Who owns the website?",
    a: "You do. Ownership terms are defined clearly in the project agreement before work begins — no surprises after launch.",
  },
];

export const insights = [
  {
    slug: "website-as-best-salesperson",
    tag: "Web Design",
    readTime: "5 min read",
    title: "Your website should be your best salesperson",
    excerpt: "Most websites describe a business. Great ones persuade for it. The difference is structure, not budget.",
    content: [
      {
        heading: "Description is not persuasion",
        body: [
          "Most business websites are written like brochures. They list services, show a few photos, and hope the visitor connects the dots. A brochure informs. A salesperson persuades. The difference is not budget or industry — it is intent.",
          "Your best salesperson does not recite a list of capabilities. They read the person in front of them, answer the question being asked, and make the next step feel obvious. A website can be built to do exactly that.",
        ],
      },
      {
        heading: "Structure is the pitch",
        body: [
          "Persuasion online happens in a predictable order: what is this, is it for me, can I trust it, what do I do next. When a page answers those four questions in that order, conversion stops being mysterious.",
          "That is why we plan the argument of a page before we design a single pixel of it. Headline, proof, detail, action — the layout is simply the pitch made visible.",
        ],
      },
      {
        heading: "The test",
        body: [
          "Show your homepage to someone who knows nothing about your business for five seconds, then take it away. If they cannot tell you what you do and who it is for, your website is describing — not selling.",
        ],
      },
    ],
  },
  {
    slug: "design-isnt-the-first-step",
    tag: "Strategy",
    readTime: "4 min read",
    title: "Design isn't the first step",
    excerpt: "Why the most important decisions about your website happen before anyone opens a design tool.",
    content: [
      {
        heading: "The instinct to start with visuals",
        body: [
          "When a business decides it needs a new website, the first conversation is usually about how it should look. Colors, fonts, inspiration links. It feels productive. It is also backwards.",
          "A website's job is to move a specific kind of visitor toward a specific action. Until you know who that visitor is and what they need to believe, every visual decision is a guess.",
        ],
      },
      {
        heading: "What comes before design",
        body: [
          "Three decisions shape everything that follows: who the site must persuade, what those visitors need to understand, and what action the experience should drive. With those answers, design becomes a series of logical choices instead of a debate about taste.",
          "This is why our process starts with discovery and strategy. Not as ceremony — as the shortest path to a website that actually works.",
        ],
      },
      {
        heading: "The cost of skipping it",
        body: [
          "Websites built design-first tend to be beautiful and ineffective. They win compliments from peers and silence from customers. Strategy-first sites may look quieter in a portfolio, but they ring the phone.",
        ],
      },
    ],
  },
  {
    slug: "the-sixty-second-test",
    tag: "Conversion",
    readTime: "6 min read",
    title: "The sixty-second test",
    excerpt: "A visitor gives you seconds, not minutes. Here's how to structure a homepage that earns the next one.",
    content: [
      {
        heading: "The reality of attention",
        body: [
          "A first-time visitor does not read your website. They scan it, forming judgments in seconds: what is this, is it for me, does it feel credible. Design is judged before a single word is properly read.",
          "This is not a problem to complain about. It is a constraint to design around.",
        ],
      },
      {
        heading: "What each second needs to do",
        body: [
          "At zero seconds, the first impression lands — craft and coherence signal whether you are worth listening to. By three seconds, the visitor should understand what the business does. By ten, trust markers should be visible. By thirty, they are exploring. By sixty, the next step should be unmissable.",
          "Every element on the page either serves one of those moments or competes with them. That is the entire discipline of homepage structure.",
        ],
      },
      {
        heading: "Earning the next second",
        body: [
          "The goal of any moment on a page is not to close — it is to earn the next moment. A headline earns the subheading. The subheading earns the scroll. The scroll earns the click. Build each one to do its single job, and the minute takes care of itself.",
        ],
      },
    ],
  },
  {
    slug: "speed-is-a-design-decision",
    tag: "Performance",
    readTime: "4 min read",
    title: "Speed is a design decision",
    excerpt: "Load time shapes perception before a single pixel is judged. Slow reads as cheap — fast reads as competent.",
    content: [
      {
        heading: "Perception starts at load time",
        body: [
          "Visitors begin judging your business before your design appears. A page that hesitates communicates something — disorganization, indifference, a company behind the times. A page that snaps open communicates the opposite.",
          "In that sense, load time is the first design element a visitor ever experiences.",
        ],
      },
      {
        heading: "Slow reads as cheap",
        body: [
          "This is uncomfortable but consistent: people associate speed with competence and sluggishness with neglect. A premium brand cannot afford a website that feels heavy, any more than a luxury store can afford a sticky front door.",
        ],
      },
      {
        heading: "How we build for it",
        body: [
          "Performance is not a cleanup task at the end of a project. It is a constraint we design inside from the start: optimized imagery, disciplined animation, lean code, and pages built to be fast on a phone connection, not just an office network.",
          "A beautiful website that loads slowly is, by our standard, poorly designed.",
        ],
      },
    ],
  },
];

export const milestones = [
  { t: 0, label: "First impression", description: "Design is judged before a word is read." },
  { t: 3, label: "Understand the business", description: "Clarity decides whether they stay." },
  { t: 10, label: "Establish trust", description: "Craft, detail, and coherence signal credibility." },
  { t: 30, label: "Explore", description: "Structure turns curiosity into consideration." },
  { t: 60, label: "Take action", description: "The next step must always be obvious." },
];
